import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "@/components/portal/Toast"

/**
 * Plan-Ausgang an der Mail (#64 Teil 0): wie der Plan endete, was stattdessen
 * geschah (Nachschau), warum (Deutung) — und der Trace des Vorgangs.
 *
 * Erscheint nur, wenn an der Mail ein Plan hängt UND er einen Ausgang hat.
 * Offene Pläne zeigt die Plan-Karte darüber. Kein Pflichtklick: der Daumen
 * zur Deutung ist ein Angebot, kein Muss.
 */

type Spur = { quelle: string; typ: string; bezeichnung: string; zeitpunkt: string | null; benutzer?: string; ueber_portal?: boolean }
type Antwort = {
  email_id: number; hat_plan: boolean; ausgang: string | null; ausgang_at: string | null
  ausgang_benutzer: string | null; ausgang_quelle: string | null
  nachschau: { stand: string; zusammenfassung: string; spuren: Spur[]; vergleich: { befund: string }[]; kontakt: { name: string } | null } | null
  deutung: { grund: string; konfidenz: number; begruendung: string; regel_vorschlag?: string; quelle: string
             bewertung?: { passt: boolean; kommentar?: string | null } } | null
  hinweis_offen?: string | null
}
type TraceSchritt = { schritt: string; modell: string | null; dauer_ms: number | null; kosten_cents: number; created_at: string | null; ausgabe_kurz: string | null; details: Record<string, unknown> | null }

export const AUSGANG_TEXT: Record<string, string> = {
  ausgefuehrt: "ausgeführt", teilweise: "teilweise ausgeführt", beantwortet: "beantwortet", weitergeleitet: "weitergeleitet",
  verworfen: "ohne Antwort erledigt", erledigt: "von Hand erledigt", abgelaufen: "liegen geblieben (7 Tage ohne Aktion)",
  anders_erledigt: "anders erledigt, von selbst geschlossen",
  nichts_noetig: "nichts nötig (nur zur Kenntnis, von selbst geschlossen)",
}
const GRUND: Record<string, string> = {
  bestandskunde_statt_neukunde: "Bestandskunde statt Neukunde", projekttyp_falsch: "Projekttyp passte nicht",
  antwort_umgeschrieben: "Antwort umgeschrieben", telefonisch_erledigt: "telefonisch erledigt", hero_direkt: "direkt in HERO erledigt",
  anderweitig_beantwortet: "anders beantwortet", kein_handlungsbedarf: "kein Handlungsbedarf", vergessen_vermutet: "vermutlich liegen geblieben",
  weitergeleitet: "intern weitergeleitet", aufgabe_statt_plan: "Aufgabe statt Plan",
  mit_hinweis_korrigiert: "mit Hinweis korrigiert", termin_umgeplant: "Termin verschoben",
  im_postfach_abgelegt: "im Postfach abgelegt", bewusst_zurueckgestellt: "bewusst zurückgestellt",
  unklar: "unklar",
}
const QUELLE_ICON: Record<string, string> = { portal: "🖱", mail: "✉️", telefon: "📞", hero: "🧰" }

const wann = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""

export function PlanAusgangBlock({ emailId }: { emailId: number }) {
  const qc = useQueryClient()
  const [traceOffen, setTraceOffen] = useState(false)
  const { data } = useQuery<Antwort>({
    queryKey: ["plan-ausgang", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/plan-ausgang/${emailId}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 30_000,
  })
  const trace = useQuery<{ schritte: TraceSchritt[]; kosten_cents: number }>({
    queryKey: ["vorgang-trace", "email", emailId],
    enabled: traceOffen,
    queryFn: async () => {
      const r = await fetch(`/api/v1/vorgang-trace/email/${emailId}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })
  const bewerten = useMutation({
    mutationFn: async (passt: boolean) => {
      const r = await fetch(`/api/v1/plan-ausgang/${emailId}/deutung-bewertung`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passt }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    onSuccess: () => { toast("Danke — fließt in den Wochenbericht ein"); qc.invalidateQueries({ queryKey: ["plan-ausgang", emailId] }) },
    onError: (e) => toast("Bewertung fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  if (!data || !data.hat_plan || !data.ausgang) return null
  const n = data.nachschau
  const d = data.deutung

  return (
    <div className="ev2-hinweiszeile" style={{ display: "block", lineHeight: 1.5 }} data-testid="plan-ausgang">
      <div>
        <strong>Plan {AUSGANG_TEXT[data.ausgang] ?? data.ausgang}</strong>
        {data.ausgang_benutzer ? ` von ${data.ausgang_benutzer}` : ""}
        {data.ausgang_at ? ` am ${wann(data.ausgang_at)}` : ""}
        {data.ausgang_quelle === "job" ? " (automatisch)" : ""}
      </div>
      {/* #19: Antwort von Hand ging raus, im Plan fehlt noch etwas. Der Plan
          darüber bleibt ausführbar, bis das erledigt ist. */}
      {data.hinweis_offen && (
        <div data-testid="plan-hinweis-offen" style={{
          marginTop: 4, color: "#791F1F", background: "#FCEBEB",
          border: "0.5px solid #F09595", borderRadius: 6, padding: "4px 8px",
        }}>
          ⚠ {data.hinweis_offen} — bitte im Plan noch ausführen.
        </div>
      )}
      {n && (
        <div style={{ marginTop: 4 }}>
          {n.zusammenfassung}
          {n.spuren.length > 0 && (
            <ul style={{ margin: "2px 0 0 14px", padding: 0 }}>
              {n.spuren.slice(0, 8).map((s, i) => (
                <li key={i}>
                  <span title={s.quelle}>{QUELLE_ICON[s.quelle] ?? "•"}</span> {s.bezeichnung}
                  {s.benutzer ? ` (${s.benutzer})` : ""}{s.zeitpunkt ? ` · ${wann(s.zeitpunkt)}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {d && (
        <div style={{ marginTop: 4 }} data-testid="plan-deutung">
          <strong>Warum (Vermutung, {Math.round(d.konfidenz * 100)} %):</strong> {GRUND[d.grund] ?? d.grund} — {d.begruendung}
          {d.regel_vorschlag ? <div style={{ fontStyle: "italic" }}>Regelidee: {d.regel_vorschlag}</div> : null}
          <span style={{ marginLeft: 8, whiteSpace: "nowrap" }}>
            {d.bewertung ? (
              <span className="text-muted-foreground">{d.bewertung.passt ? "👍 bestätigt" : "👎 widersprochen"}</span>
            ) : (
              <>
                <button type="button" className="pp-btn pp-btn-sm" title="Deutung passt" disabled={bewerten.isPending} onClick={() => bewerten.mutate(true)}>👍</button>{" "}
                <button type="button" className="pp-btn pp-btn-sm" title="Deutung passt nicht" disabled={bewerten.isPending} onClick={() => bewerten.mutate(false)}>👎</button>
              </>
            )}
          </span>
        </div>
      )}
      <div style={{ marginTop: 4 }}>
        <button type="button" className="pp-btn pp-btn-sm" onClick={() => setTraceOffen((o) => !o)}>
          {traceOffen ? "Trace ausblenden" : "Trace des Vorgangs"}
        </button>
        {traceOffen && trace.data && (
          <ul style={{ margin: "4px 0 0 14px", padding: 0, fontSize: 12 }} data-testid="plan-trace">
            {trace.data.schritte.length === 0 && <li>Noch kein Schritt aufgezeichnet (Trace läuft seit dem 11.09.2026).</li>}
            {trace.data.schritte.map((s, i) => (
              <li key={i}>
                {wann(s.created_at)} · <strong>{s.schritt}</strong>
                {s.modell ? ` · ${s.modell}` : ""}{s.dauer_ms != null ? ` · ${(s.dauer_ms / 1000).toFixed(1)} s` : ""}
                {s.kosten_cents ? ` · ${s.kosten_cents.toFixed(2)} ¢` : ""}
                {s.details && !s.modell ? ` · ${Object.entries(s.details).filter(([, v]) => v != null && typeof v !== "object").map(([k, v]) => `${k}=${String(v)}`).join(", ")}` : ""}
              </li>
            ))}
            {trace.data.schritte.length > 0 && <li className="text-muted-foreground">Summe {trace.data.kosten_cents.toFixed(2)} ¢</li>}
          </ul>
        )}
      </div>
    </div>
  )
}
